import { type Infer, v } from "convex/values";
import { SUPPORT_AGENT_KEY_SCOPES } from "../../../shared/support/agent-mcp";
import {
  AGENT_FIX_REPORTED_STATUSES,
  AGENT_FIX_EVENTS,
  AGENT_FIX_STARTERS,
  AGENT_FIX_STATUSES,
  AGENT_FIX_TRUST_LEVELS,
} from "../../../shared/support/agent-fix";
import { SUPPORT_TICKET_RELATIONS } from "../../constants/support";

export const supportRequestStatuses = [
  "pending",
  "in_progress",
  "resolved",
  "closed",
] as const;
export const supportRequestStatusValidator = v.union(
  ...supportRequestStatuses.map((status) => v.literal(status)),
);
export type SupportRequestStatus = Infer<typeof supportRequestStatusValidator>;

export const supportTerminalStatuses = ["resolved", "closed"] as const;
export const supportTerminalStatusValidator = v.union(
  ...supportTerminalStatuses.map((status) => v.literal(status)),
);
export type SupportTerminalStatus = Infer<
  typeof supportTerminalStatusValidator
>;

export const supportWorkStates = [
  "unclaimed",
  "investigating",
  "implementing",
  "blocked",
  "in_review",
  "awaiting_verification",
  "complete",
] as const;
export const supportWorkStateValidator = v.union(
  ...supportWorkStates.map((workState) => v.literal(workState)),
);
export type SupportWorkState = Infer<typeof supportWorkStateValidator>;

export const supportAgentProviders = ["codex", "claude", "other"] as const;
export const supportAgentProviderValidator = v.union(
  ...supportAgentProviders.map((provider) => v.literal(provider)),
);
export type SupportAgentProvider = Infer<typeof supportAgentProviderValidator>;

export const supportAgentKeyScopeValidator = v.union(
  ...SUPPORT_AGENT_KEY_SCOPES.map((scope) => v.literal(scope)),
);

export const supportAgentClaimValidator = v.object({
  provider: supportAgentProviderValidator,
  runId: v.optional(v.string()),
  runUrl: v.optional(v.string()),
  model: v.optional(v.string()),
  claimedAt: v.optional(v.number()),
  principal: v.optional(v.string()),
});

export const ticketRequesterKinds = ["person", "agent", "system"] as const;
export const ticketRequesterKindValidator = v.union(
  ...ticketRequesterKinds.map((kind) => v.literal(kind)),
);

export const supportFiledByAgentValidator = v.object({
  provider: supportAgentProviderValidator,
  model: v.optional(v.string()),
});
export type SupportFiledByAgent = Infer<typeof supportFiledByAgentValidator>;

export const supportAgentFixStatusValidator = v.union(
  ...AGENT_FIX_STATUSES.map((status) => v.literal(status)),
);

export const supportAgentFixReportedStatusValidator = v.union(
  ...AGENT_FIX_REPORTED_STATUSES.map((status) => v.literal(status)),
);

export const supportAgentFixStarterValidator = v.union(
  ...AGENT_FIX_STARTERS.map((starter) => v.literal(starter)),
);

export const supportAgentFixEventValidator = v.union(
  ...AGENT_FIX_EVENTS.map((event) => v.literal(event)),
);

export const supportAgentFixTrustValidator = v.union(
  ...AGENT_FIX_TRUST_LEVELS.map((trust) => v.literal(trust)),
);

export const supportAgentFixValidator = v.object({
  requestedBy: v.optional(v.id("users")),
  requestedByKey: v.optional(v.id("supportAgentApiKeys")),
  requestedVia: v.optional(supportAgentFixStarterValidator),
  event: v.optional(supportAgentFixEventValidator),
  trust: v.optional(supportAgentFixTrustValidator),
  attempt: v.optional(v.number()),
  priorFailure: v.optional(v.string()),
  dispatchDay: v.optional(v.string()),
  dispatchCount: v.optional(v.number()),
  requestedAt: v.number(),
  status: supportAgentFixStatusValidator,
  statusAt: v.number(),
  note: v.optional(v.string()),
  runUrl: v.optional(v.string()),
  pullRequestUrl: v.optional(v.string()),
});
export type SupportAgentFix = Infer<typeof supportAgentFixValidator>;

export const agentFixAutostartSettingsValidator = v.object({
  enabled: v.boolean(),
  maxPerDay: v.number(),
  maxPerTicketPerDay: v.number(),
  maxChainDepth: v.number(),
});
export type AgentFixAutostartSettings = Infer<typeof agentFixAutostartSettingsValidator>;

export const agentFixAutostartUsageValidator = v.object({
  day: v.string(),
  count: v.number(),
  capAnnounced: v.optional(v.boolean()),
});
export type AgentFixAutostartUsage = Infer<typeof agentFixAutostartUsageValidator>;

export const SUPPORT_CLIENT_CONTEXT_FAILURE_LIMIT = 5;

export const supportClientConvexFailureValidator = v.object({
  requestId: v.string(),
  functionName: v.optional(v.string()),
  failedAt: v.string(),
});

export const supportClientContextValidator = v.object({
  release: v.optional(v.string()),
  environment: v.optional(v.string()),
  landingRequestId: v.optional(v.string()),
  convexFailures: v.optional(v.array(supportClientConvexFailureValidator)),
  viewport: v.optional(v.string()),
  timezone: v.optional(v.string()),
  language: v.optional(v.string()),
});
export type SupportClientContext = Infer<typeof supportClientContextValidator>;

export const errorSourceValidator = v.literal("posthog");
export type ErrorSource = Infer<typeof errorSourceValidator>;

export const supportGithubLinkStates = [
  "open",
  "closed",
  "merged",
  "draft",
] as const;
export const supportGithubLinkStateValidator = v.union(
  ...supportGithubLinkStates.map((state) => v.literal(state)),
);
export type SupportGithubLinkState = Infer<
  typeof supportGithubLinkStateValidator
>;

export const supportResolutionCodeValidator = v.union(
  v.literal("fixed"),
  v.literal("duplicate"),
  v.literal("answered"),
  v.literal("not_reproducible"),
  v.literal("declined"),
  v.literal("spam"),
  v.literal("other"),
);
export type SupportResolutionCode = Infer<
  typeof supportResolutionCodeValidator
>;

export const supportPriorities = ["low", "medium", "high", "urgent"] as const;
export const supportPriorityValidator = v.union(
  ...supportPriorities.map((priority) => v.literal(priority)),
);
export type SupportPriority = Infer<typeof supportPriorityValidator>;

export const supportKindValidator = v.union(
  v.literal("general"),
  v.literal("bug"),
  v.literal("order_issue"),
  v.literal("feedback"),
  v.literal("billing"),
  v.literal("shipping"),
  v.literal("account"),
  v.literal("technical"),
  v.literal("sales"),
);
export type SupportKind = Infer<typeof supportKindValidator>;

export const supportTicketTypeValidator = v.union(
  v.literal("bug"),
  v.literal("idea"),
  /** @deprecated Folded into "idea" — read-only until the backfill narrows this union. */
  v.literal("feature_request"),
  /** @deprecated Folded into "idea" — read-only until the backfill narrows this union. */
  v.literal("question"),
  /** @deprecated Folded into "idea" — read-only until the backfill narrows this union. */
  v.literal("task"),
);
export type SupportTicketType = Infer<typeof supportTicketTypeValidator>;

export const SUPPORT_TICKET_TYPES = ["bug", "idea"] as const;
export const RETIRED_SUPPORT_TICKET_TYPES = [
  "feature_request",
  "question",
  "task",
] as const;
export type SupportTicketTypeWritable = (typeof SUPPORT_TICKET_TYPES)[number];
export const supportWritableTicketTypeValidator = v.union(
  ...SUPPORT_TICKET_TYPES.map((type) => v.literal(type)),
);

export function resolveSupportTicketType(
  type: SupportTicketType | undefined,
): SupportTicketTypeWritable {
  return type === "bug" ? "bug" : "idea";
}

export const supportProjectStatuses = [
  "planned",
  "active",
  "paused",
  "done",
] as const;
export const supportProjectStatusValidator = v.union(
  ...supportProjectStatuses.map((status) => v.literal(status)),
);
export type SupportProjectStatus = Infer<typeof supportProjectStatusValidator>;

export const supportSources = [
  "public_form",
  "in_app",
  "ai_chat",
  "deal_button",
  "error_alert",
  "feedback_prompt",
  "order_issue",
  "issue_report",
  "email",
  "slack",
  "agent",
  "sms",
  "phone",
] as const;
export const supportSourceValidator = v.union(
  ...supportSources.map((source) => v.literal(source)),
);
export type SupportSource = Infer<typeof supportSourceValidator>;

export const supportTicketRelationValidator = v.union(
  ...SUPPORT_TICKET_RELATIONS.map((relation) => v.literal(relation)),
);
